import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ProgressSpinner } from 'primeng/progressspinner';
import { AppToastComponent } from './shared/components/app-toast/app-toast';
import { LanguageService } from './core/services/language';
import { LoadingService } from './core/services/loading.service';
import { AppUpdateService } from './core/services/app-update.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, ProgressSpinner, AppToastComponent, TranslateModule],
  // Gabarit et styles en ligne, comme partout ailleurs dans ce projet. Ce
  // composant était le seul à passer par `templateUrl`/`styleUrl`, ce que le
  // banc Vitest ne sait pas résoudre : son test échouait sur « Component 'App'
  // is not resolved » au lieu de tester quoi que ce soit.
  template: `
    <!-- Toast global de l'application — modèle unique partagé partout. -->
    <app-toast />

    @if (isLoading()) {
      <div class="app-http-loader-bar"></div>
    }

    @if (showBlockingLoader()) {
      <div class="app-global-loader-backdrop" aria-live="polite" aria-busy="true">
        <div class="app-global-loader-card">
          <p-progressspinner strokeWidth="4" styleClass="app-global-spinner" ariaLabel="Chargement en cours" />
          <div class="app-global-loader-text">
            <strong>Chargement en cours...</strong>
            <span>{{ 'MISC.PLEASE_WAIT' | translate }}</span>
          </div>
        </div>
      </div>
    }

    @if (updateService.updateReady()) {
      <div
        class="fixed bottom-4 left-1/2 z-[10000] flex -translate-x-1/2 items-center gap-3 rounded-full border
               border-primary-border bg-primary-surface px-4 py-2 shadow-lg"
        role="status"
      >
        <i class="pi pi-arrow-circle-up text-primary-base" aria-hidden="true"></i>
        <span class="text-sm font-semibold text-primary-text">{{ 'MISC.NEW_VERSION' | translate }}</span>
        <button
          type="button"
          class="rounded-full bg-primary-base px-3 py-1 text-xs font-bold text-on-primary"
          (click)="updateService.applyUpdate()"
        >
          Actualiser
        </button>
      </div>
    }

    <router-outlet />
  `,
  styles: [
    `
    :host {
      display: block;
    }

    .app-toast-message {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      width: 100%;
      padding: 0.25rem;
    }

    .app-toast-icon {
      flex-shrink: 0;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .app-toast-icon-success {
      background: #d1fae5;
      color: #059669;
    }

    .app-toast-icon-error {
      background: #fee2e2;
      color: #dc2626;
    }

    .app-toast-icon-warn {
      background: #fef3c7;
      color: #d97706;
    }

    .app-toast-icon-info {
      background: #dbeafe;
      color: #2563eb;
    }

    .app-toast-content {
      flex: 1;
      min-width: 0;
      padding-top: 0.125rem;
    }

    .app-toast-summary {
      font-weight: 700;
      color: #0f172a;
    }

    .app-toast-detail {
      font-size: 0.875rem;
      color: #64748b;
      margin-top: 0.125rem;
    }

    .app-http-loader-bar {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 3px;
      z-index: 6000;
      background: linear-gradient(90deg, var(--color-primary-base) 0%, var(--color-primary-500) 50%, var(--color-success-base) 100%);
      background-size: 200% 100%;
      animation: app-http-loader-slide 1.1s linear infinite;
      box-shadow: none;
    }

    .app-global-loader-backdrop {
      position: fixed;
      inset: 0;
      z-index: 5500;
      display: flex;
      align-items: center;
      justify-content: center;
      background: color-mix(in srgb, var(--color-ink) 14%, transparent);
      backdrop-filter: blur(2px);
      pointer-events: none;
    }

    .app-global-loader-card {
      min-width: 280px;
      max-width: min(90vw, 360px);
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem 1.25rem;
      border-radius: 1rem;
      background: var(--color-panel);
      border: 1px solid var(--color-hairline);
      box-shadow: 0 20px 60px rgba(15, 23, 42, 0.16);
    }

    .app-global-spinner {
      width: 2.75rem !important;
      height: 2.75rem !important;
    }

    .app-global-loader-text {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      color: var(--color-ink);
    }

    .app-global-loader-text strong {
      font-size: 0.95rem;
    }

    .app-global-loader-text span {
      font-size: 0.82rem;
      color: var(--color-ink-muted);
    }

    @keyframes app-http-loader-slide {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
    `,
  ],
})
export class App implements OnInit {
  protected readonly title = signal('frontend');
  private languageService = inject(LanguageService);
  protected readonly loadingService = inject(LoadingService);
  protected readonly updateService = inject(AppUpdateService);
  protected readonly isLoading = this.loadingService.isLoading;
  protected readonly showBlockingLoader = computed(() => this.loadingService.pendingCount() > 0);

  ngOnInit(): void {
    this.languageService.init();
  }
}
